import { useEffect, useMemo } from 'react'
import {
  DeviceUrl,
  SendDigitalSignatureRequestClientRequest,
} from '../../@types/digitalSignature/sendDigitalSignature'
import { useContextStore } from '../../Stores/contextStore'
import { useDigitalSignatureSettingsStore } from '../../Stores/digitalSignature/useDigitalSignatureSettingsStore'
import { useSignWithDigitalDeviceStore } from '../../Stores/digitalSignature/useSignWithDigitalDeviceStore'
import { useAgent } from '../../Stores/useAgent'
import { useCreateProposalStore } from '../../Stores/useCreateProposalStore'
import { useSendDigitalSignatureStore } from '../../Stores/useSendDigitalSignatureStore'
import { useSendDigitalSignature } from '../../hooks/api/digitalSignature/useSendDigitalSignatureRequest'
import { useSignWithDigitalDevice } from '../../hooks/api/digitalSignature/useSignWithDigitalDevice'
import { EMAIL_OPTION_CODE } from './Expanded'

type Props = {
  email: string
  phone: string
}

export const usePayloadSendDIgitalSignature = ({ email, phone }: Props) => {
  const { agentIdBySessionStorage } = useAgent()
  const { globalContext } = useContextStore()
  const { sendDigitalSignature } = useSendDigitalSignatureStore()
  const { signWithDigitalDevice } = useSignWithDigitalDeviceStore()
  const {
    mutate: signWithDigitalDeviceMutate,
    isSuccess: isSuccessSignWithDigitalDevice,
    isLoading: isLoadingSignWithDigitalDevice,
    reset: resetSignWithDigitalDevice,
  } = useSignWithDigitalDevice()
  const { settings, signatureTypeSelected } = useDigitalSignatureSettingsStore()
  const { createProposal } = useCreateProposalStore()
  const { mutate, isError, isSuccess, isLoading } = useSendDigitalSignature()

  const isSMSSignature =
    signatureTypeSelected?.signatureType === EMAIL_OPTION_CODE

  const payloadSignWithDigitalDevice = useMemo(
    () => ({
      signatureId: sendDigitalSignature?.digitalSignature?.signatureId || '',
    }),
    [sendDigitalSignature],
  )

  const payload: SendDigitalSignatureRequestClientRequest = {
    digitalSignature: {
      policyID: createProposal?.createProposalResponse?.proposalId || '',
      participants:
        settings?.participants?.map((item) => ({
          signatureType: signatureTypeSelected?.signatureType || '',
          participantId: item?.participantId || '',
          participantEmail: isSMSSignature
            ? email
            : item.participantEmail || '',
          participantPhoneNumber: isSMSSignature
            ? phone
            : item.participantPhoneNumber || '',
          signaturePosition:
            item?.signaturePosition?.map((signaturePosition) => ({
              documentId: signaturePosition.documentId || 0,
              page: signaturePosition.page || 1,
              xPosition: signaturePosition.xPosition || 0,
              yPosition: signaturePosition.yPosition || 0,
              width: signaturePosition.width || 0,
              height: signaturePosition.height || 0,
              required: signaturePosition.required || true,
            })) || [],
        })) || [],
      user: globalContext?.username || '',
      mediationAccount: agentIdBySessionStorage || '',
    },
  }

  const handleSendDigitalSignature = () => {
    mutate(payload)
  }

  const handleSignWithDigitalDeviceMutate = () => {
    signWithDigitalDeviceMutate(payloadSignWithDigitalDevice)
  }

  useEffect(() => {
    if (
      isSuccessSignWithDigitalDevice &&
      signatureTypeSelected?.signatureType !== EMAIL_OPTION_CODE
    ) {
      let link: DeviceUrl = {}
      const device = navigator.userAgent.toLowerCase()
      const getUserDevice = (type: string) =>
        signWithDigitalDevice?.digitalSignature?.participants?.[0].deviceUrl?.find(
          (item) => item?.device === type,
        )

      if (device === 'android' || device === 'iphone' || device === 'ipad') {
        link = getUserDevice(device)!
      } else {
        link = getUserDevice('Web')!
      }

      window.open(link?.url as string, '_blank')
      resetSignWithDigitalDevice()
    }
  }, [
    signatureTypeSelected,
    isSuccessSignWithDigitalDevice,
    sendDigitalSignature,
    signWithDigitalDevice,
  ])

  return {
    handleSendDigitalSignature,
    isErrorSendDigitalSignature: isError,
    isSuccessSendDigitalSignature: isSuccess,
    isLoadingSendDigitalSignature: isLoading,
    handleSignWithDigitalDeviceMutate,
    isSuccessSignWithDigitalDevice,
    isLoadingSignWithDigitalDevice,
  }
}
